import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  interval,
  Observable,
  Subject,
  switchMap,
  takeUntil,
  takeWhile,
} from 'rxjs';
import type {
  TenderFormAnswerEvaluationProgress,
  TenderFormAnswerEvaluationProgressStatus,
} from '../../api/interfaces';

/** How often the backend is asked for evaluation progress. */
export const EVALUATION_POLL_INTERVAL_MS = 5000;

/** Statuses that mean an evaluation is still running. */
export const EVALUATION_IN_PROGRESS_STATUSES: readonly TenderFormAnswerEvaluationProgressStatus[] =
  ['Queued', 'Scoring', 'Evaluating'];

/** True while the evaluation is queued or running. */
export function isEvaluationInProgress(
  status: TenderFormAnswerEvaluationProgressStatus,
): boolean {
  return EVALUATION_IN_PROGRESS_STATUSES.includes(status);
}

/**
 * Polls an AI evaluation to completion.
 *
 * Provide it alongside the page component, not in root: it is tied to one
 * evaluation and stops with the component that injects it.
 */
@Injectable()
export class EvaluationPollingService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly stopPolling$ = new Subject<void>();

  /**
   * Polls `getProgress` until the evaluation reports `Completed` or `Failed`,
   * emitting every progress update along the way. Starting a new poll cancels
   * any poll already in flight, and everything stops when the host component
   * is destroyed.
   */
  poll(
    getProgress: () => Observable<TenderFormAnswerEvaluationProgress>,
  ): Observable<TenderFormAnswerEvaluationProgress> {
    this.stopPolling$.next();

    return interval(EVALUATION_POLL_INTERVAL_MS).pipe(
      switchMap(() => getProgress()),
      // `true` keeps the terminal status, so subscribers see the final state.
      takeWhile((p) => p.status !== 'Completed' && p.status !== 'Failed', true),
      takeUntil(this.stopPolling$),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  /** Stops the current poll without destroying the service. */
  stop(): void {
    this.stopPolling$.next();
  }
}
